import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import CustomModal from '../../../components/CustomModal';
import {
  STATUS_HEX,
  STATUS_LABEL,
  info,
  normalizeStatus,
  typeIconSVG,
  tripStatusOf,
  TRIP_META,
  keyMetric,
  etaLine,
  ignChip,
  ic,
  todaySplitOf,
} from './trackingUtils';
import VehicleTrackingMap from './VehicleTrackingMap';
import VehicleTrackingCard from './VehicleTrackingCard';
import VehicleTrackingList from './VehicleTrackingList';
import VehicleTrackingHistory from './VehicleTrackingHistory';
import VehicleTrackingSummaryStrip from './VehicleTrackingSummaryStrip';
import { useDispatch } from 'react-redux';
import VehicleTrackingFilterPop from './VehicleTrackingFilterPop';
import VehicleTrackingVehiclePicker from './VehicleTrackingVehiclePicker';
import { apiService } from '../../../services/apiService';
import { useTripSocket } from '../../../utils/SocketTripContext';
import defaultTripSocket from '../../../utils/trip-socket';
import customSocket from '../../../utils/custom-socket';
import {
  setTrackingVehicles,
  setSelectedTrackingVehicle,
  updateVehicleLocationFromSocket,
  addGlobalViolationAlert,
  addTripViolationAlert,
  addVehicleViolationAlert,
  setTripNewLogsSignal,
  setSocketHealthStatus,
  clearSelectedVehicle,
} from '../../../store/Slices/VehicleTrackingSlice';
import { updateTripDetailsLiveData } from '../../../store/Slices/TripSlice';
import {
  mapApiVehicleToTrackingVehicle,
  getHistoryDateRange,
  mapApiVehicleHistoryToHistoryData,
  mapApiHistorySummaryToFleetData,
  mergeVehicleWithViewData,
  decodePolyline,
  normalizeViolation,
  computeBearing,
  getRouteHeading,
  saveCachedVehicleCoords,
} from './vehicleTracking.adapter';
import '../../../styles/VehicleTracking.css';

// Filter Matching Function
const matchesFilters = (v, f = {}) => {
  const i = info(v);
  if (f.cat && v.cat !== f.cat) return false;
  if (f.type && v.type !== f.type) return false;
  if (f.cust && v.cust !== f.cust) return false;
  if (f.drv && v.drv !== f.drv) return false;
  if (f.prov && i.prov?.id !== f.prov) return false;
  if (f.trip) {
    if (f.trip === 'on' && !i.onTrip) return false;
    if (f.trip === 'none' && i.onTrip) return false;
  }
  if (f.tx) {
    if (f.tx === 'delayed' && (!i.onTrip || i.status === 'moving')) return false;
    if (f.tx === 'overdue' && !v.overdue) return false;
    if (f.tx === 'offtrip' && !(i.tripStatus === '—' && i.status === 'moving' && !i.offline))
      return false;
    if (f.tx === 'vio' && (!v.vio || !v.vio.length)) return false;
  }
  return true;
};

const VehicleTracking = () => {
  const dispatch = useDispatch();
  const socketCtx = useTripSocket();
  const activeTripSocket = socketCtx?.tripSocket || defaultTripSocket;

  // Listen to both tracking-plus-sync-service and api-maintenance sockets simultaneously
  const activeSockets = useMemo(() => {
    const list = [];
    if (activeTripSocket) list.push(activeTripSocket);
    if (customSocket && !list.includes(customSocket)) list.push(customSocket);
    return list;
  }, [activeTripSocket]);

  const activeSocket = activeTripSocket;
  const [mode, setMode] = useState('live'); // 'live' | 'history'
  const [view, setView] = useState('map'); // 'map' | 'list'
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedReg, setSelectedReg] = useState(null);
  const [expandedReg, setExpandedReg] = useState(null);
  const [followVehicle, setFollowVehicle] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [mapOnly, setMapOnly] = useState(false);
  const [flyTrigger, setFlyTrigger] = useState(1);
  const [visibleLiveCount, setVisibleLiveCount] = useState(20);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportingTopPdf, setExportingTopPdf] = useState(false);

  // Fetch live vehicles from backend API and hydrate real GPS coordinates via getFmcrVehiclesView
  useEffect(() => {
    let isMounted = true;
    const fetchVehicles = async () => {
      try {
        setLoadingVehicles(true);
        const res = await apiService.getFmcrVehicles({ status: 'all', limit: 100 });
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.results)
          ? res.data.results
          : Array.isArray(res?.results)
          ? res.results
          : [];

        if (!isMounted) return;

        if (list.length > 0) {
          const mapped = list.map(mapApiVehicleToTrackingVehicle).filter(Boolean);
          setVehicles(mapped);
          saveCachedVehicleCoords(mapped);

          // Progressively hydrate all vehicles in parallel chunks of 15 to avoid database lockups & delay
          const vehicleIds = list.map((item) => item.id || item.vehicle_id).filter(Boolean);
          if (vehicleIds.length > 0) {
            const chunkSize = 15;
            for (let i = 0; i < vehicleIds.length; i += chunkSize) {
              if (!isMounted) break;
              const chunk = vehicleIds.slice(i, i + chunkSize);
              try {
                const viewRes = await apiService.getFmcrVehiclesView({ vehicle_Ids: chunk });
                const viewList = Array.isArray(viewRes?.data)
                  ? viewRes.data
                  : Array.isArray(viewRes)
                  ? viewRes
                  : [];

                if (isMounted && viewList.length > 0) {
                  const viewMap = new Map();
                  viewList.forEach((it) => {
                    if (it && it.id) viewMap.set(it.id, it);
                  });

                  setVehicles((prev) => {
                    const updated = prev.map((v) => {
                      const viewItem = viewMap.get(v.id);
                      return viewItem ? mergeVehicleWithViewData(v, viewItem) : v;
                    });
                    saveCachedVehicleCoords(updated);
                    return updated;
                  });
                }
              } catch (viewErr) {
                console.warn('Failed to hydrate vehicles view chunk:', viewErr);
              }
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load vehicles from API:', err);
      } finally {
        if (isMounted) setLoadingVehicles(false);
      }
    };

    fetchVehicles();
    return () => {
      isMounted = false;
    };
  }, []);

  // Real-time live coordinates polling every 10 seconds in Live mode
  useEffect(() => {
    if (mode !== 'live' || vehicles.length === 0) return;

    const interval = setInterval(async () => {
      const vehicleIds = vehicles.map((v) => v.id).filter(Boolean);
      if (vehicleIds.length === 0) return;
      try {
        const viewRes = await apiService.getFmcrVehiclesView({ vehicle_Ids: vehicleIds });
        const viewList = Array.isArray(viewRes?.data)
          ? viewRes.data
          : Array.isArray(viewRes)
          ? viewRes
          : [];

        if (viewList.length > 0) {
          const viewMap = new Map();
          viewList.forEach((it) => {
            if (it && it.id) viewMap.set(it.id, it);
          });

          setVehicles((prev) => {
            const updated = prev.map((v) => {
              const viewItem = viewMap.get(v.id);
              return viewItem ? mergeVehicleWithViewData(v, viewItem) : v;
            });
            saveCachedVehicleCoords(updated);
            return updated;
          });
        }

        // Also refresh selected vehicle tracking card during polling so card stats stay live
        if (selectedReg) {
          const selV = vehicles.find((v) => v.reg === selectedReg);
          if (selV?.id) {
            apiService
              .getFmcrVehicleTrackingCard(selV.id)
              .then((cardRes) => {
                const tele = cardRes?.data || cardRes;
                if (tele) {
                  setTelemetryCache((prev) => ({
                    ...prev,
                    [selV.id]: {
                      ...(prev[selV.id] || {}),
                      tele,
                    },
                  }));
                }
              })
              .catch(() => {});
          }
        }
      } catch (pollErr) {
        // Silent polling error
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [mode, vehicles.length, selectedReg]);

  // Exit fullscreen on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mapOnly) {
        setMapOnly(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mapOnly]);

  // Filters State
  const [filters, setFilters] = useState({
    cat: '',
    type: '',
    cust: '',
    drv: '',
    prov: '',
    trip: '',
    tx: '',
  });

  // KPI Active Filter Keys
  const [activeKpiFilter, setActiveKpiFilter] = useState(''); // 'delayed' | 'overdue' | 'offtrip' | 'vio'

  // Popover References & Visibility
  const [filterPopOpen, setFilterPopOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [pickerPopOpen, setPickerPopOpen] = useState(false);
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [triggerFitTrip, setTriggerFitTrip] = useState(null);

  // History Mode Specific States
  const [focusedReg, setFocusedReg] = useState(null); // null = fleet, string = single vehicle
  const [historyPeriod, setHistoryPeriod] = useState('today'); // 'today' | 'yesterday' | '7d' | 'custom'
  const [customRange, setCustomRange] = useState({ from: '2026-10-08', to: '2026-10-10' });
  const [selectedVehicleRegs, setSelectedVehicleRegs] = useState([]);
  const [historyHoveredReg, setHistoryHoveredReg] = useState(null);
  const [playbackStep, setPlaybackStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(4);

  // History API State
  const [historyApiData, setHistoryApiData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyReloadTrigger, setHistoryReloadTrigger] = useState(0);

  // History Summary & Tracking Plus Vehicles State
  const [trackingPlusVehicles, setTrackingPlusVehicles] = useState([]);
  const [historySummaryList, setHistorySummaryList] = useState([]);
  const [loadingHistorySummary, setLoadingHistorySummary] = useState(false);
  const [historySummaryError, setHistorySummaryError] = useState(null);

  const ltFilterBtnRef = useRef(null);
  const hFilterBtnRef = useRef(null);
  const vpBtnRef = useRef(null);

  // Fix right dashboard height and eliminate page-level scrollbar
  useEffect(() => {
    const rightDash = document.querySelector('.right_dashboard');
    if (rightDash) {
      rightDash.classList.add('vehicle_tracking_active');
    }
    return () => {
      if (rightDash) {
        rightDash.classList.remove('vehicle_tracking_active');
      }
    };
  }, []);

  // Live vehicles filtered by chips, filters & KPI buttons
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      const i = info(v);

      if (statusFilter !== 'all' && i.status !== statusFilter) return false;

      // Filter popover criteria
      if (!matchesFilters(v, filters)) return false;

      // KPI button criteria
      if (activeKpiFilter === 'delayed' && (!i.onTrip || i.status === 'moving')) return false;
      if (activeKpiFilter === 'overdue' && !v.overdue) return false;
      if (
        activeKpiFilter === 'offtrip' &&
        !(i.tripStatus === '—' && (i.status === 'moving' || (v.spd && v.spd > 0)) && !i.offline)
      )
        return false;
      if (activeKpiFilter === 'vio' && (!v.vio || !v.vio.length)) return false;

      return true;
    });
  }, [vehicles, statusFilter, filters, activeKpiFilter]);

  // History Vehicles Scope
  const historyScopeVehicles = useMemo(() => {
    let list = vehicles;
    if (selectedVehicleRegs.length > 0) {
      const regSet = new Set(selectedVehicleRegs);
      list = list.filter((v) => regSet.has(v.reg));
    }
    return list.filter((v) => matchesFilters(v, filters));
  }, [vehicles, selectedVehicleRegs, filters]);

  // Combined list of vehicles for History Mode (combines Tracking Plus dropdown + loaded vehicles)
  const historyPickerVehicles = useMemo(() => {
    if (!trackingPlusVehicles || trackingPlusVehicles.length === 0) {
      return vehicles;
    }
    const knownRegMap = new Map();
    vehicles.forEach((v) => {
      if (v.reg) knownRegMap.set(String(v.reg).toUpperCase(), v);
      if (v.id) knownRegMap.set(String(v.id), v);
    });

    return trackingPlusVehicles.map((tp) => {
      const regUpper = String(tp.label || '').toUpperCase();
      const existing = knownRegMap.get(regUpper) || knownRegMap.get(String(tp.value));
      if (existing) {
        return { ...existing, id: tp.value || existing.id, reg: regUpper };
      }
      return {
        id: tp.value,
        reg: regUpper,
        drv: 'Unassigned',
        type: 'Vehicle',
        cat: 'HTV',
        st: 'stopped',
        km: parseFloat(tp.odm) || 0,
        spd: 0,
      };
    });
  }, [trackingPlusVehicles, vehicles]);

  // Fleet Telemetry Data List (Derived directly from real API history summary when available)
  const fleetHistoryData = useMemo(() => {
    if (historySummaryList && historySummaryList.length > 0) {
      return mapApiHistorySummaryToFleetData(historySummaryList, historyPickerVehicles);
    }
    const { from, to } = getHistoryDateRange(historyPeriod, customRange);
    const fromTs = new Date(from.replace(' ', 'T')).getTime() || Date.now() - 86400000;
    const toTs = new Date(to.replace(' ', 'T')).getTime() || Date.now();
    return historyScopeVehicles.map((v) => ({
      v,
      km: v.km || 0,
      mv: v.st === 'moving' ? 1 : 0,
      idle: v.st === 'idle' ? 1 : 0,
      stp: v.st === 'stopped' ? 1 : 0,
      stops: v.stops?.length || 0,
      endSt: v.st || 'stopped',
      endP: v.lng && v.lat ? [v.lng, v.lat] : [67.0, 24.8],
      trips: v.trip ? [v.trip] : [],
      custs: v.cust ? [v.cust] : [],
      avg: v.spd || 0,
      max: 0,
      fuel: v.km ? Math.round(v.km / 3.5) : 0,
      vio: 0,
      runs: [],
      segs: [],
      H: {
        from: fromTs,
        to: toTs,
        pts: v.lng && v.lat ? [{ p: [v.lng, v.lat], t: v.last, spd: v.spd, ign: v.ignition, location: v.loc }] : [],
      },
    }));
  }, [historySummaryList, historyPickerVehicles, historyScopeVehicles, historyPeriod, customRange]);

  // Focused History Vehicle Telemetry
  const focusedVehicleObj = useMemo(() => {
    if (!focusedReg) return null;
    return vehicles.find((v) => v.reg === focusedReg) || null;
  }, [vehicles, focusedReg]);

  // Fetch tracking-plus dropdown vehicles for history mode
  useEffect(() => {
    let isMounted = true;
    apiService
      .getVehiclesTrackingPlusDropdown()
      .then((res) => {
        if (!isMounted) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setTrackingPlusVehicles(list);
      })
      .catch((err) => {
        console.error('Failed to fetch tracking plus vehicles dropdown:', err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch real aggregated vehicle history summary for History Mode
  useEffect(() => {
    if (mode !== 'history') return;

    let isMounted = true;
    const { from, to } = getHistoryDateRange(historyPeriod, customRange);

    let targetIds = [];
    if (selectedVehicleRegs.length > 0) {
      const regUpperSet = new Set(selectedVehicleRegs.map((r) => String(r).toUpperCase()));
      trackingPlusVehicles.forEach((tp) => {
        if (regUpperSet.has(String(tp.label || '').toUpperCase()) && tp.value) {
          targetIds.push(tp.value);
        }
      });
      vehicles.forEach((v) => {
        if (regUpperSet.has(String(v.reg || '').toUpperCase()) && v.id) {
          if (!targetIds.includes(v.id)) targetIds.push(v.id);
        }
      });
    } else {
      if (vehicles.length > 0) {
        targetIds = vehicles.map((v) => v.id).filter(Boolean);
      } else if (trackingPlusVehicles.length > 0) {
        targetIds = trackingPlusVehicles.map((tp) => tp.value).filter(Boolean);
      }
    }

    if (!targetIds.length) {
      setLoadingHistorySummary(false);
      return;
    }

    setLoadingHistorySummary(true);
    setHistorySummaryError(null);

    apiService
      .getFmcrVehicleHistorySummary({
        vehicle_ids: targetIds,
        from,
        to,
      })
      .then((res) => {
        if (!isMounted) return;
        const rawList = Array.isArray(res?.data?.list)
          ? res.data.list
          : Array.isArray(res?.list)
          ? res.list
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        setHistorySummaryList(rawList);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Failed to fetch vehicle history summary:', err);
        setHistorySummaryError(
          typeof err === 'string'
            ? err
            : err?.message || 'Failed to load vehicle history summary'
        );
      })
      .finally(() => {
        if (isMounted) setLoadingHistorySummary(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    mode,
    historyPeriod,
    customRange,
    selectedVehicleRegs,
    trackingPlusVehicles,
    vehicles,
    historyReloadTrigger,
  ]);

  // Fetch real history telemetry when vehicle is focused in History mode
  useEffect(() => {
    if (mode !== 'history') return;

    const targetVehicle =
      focusedVehicleObj ||
      (focusedReg ? vehicles.find((v) => v.reg === focusedReg) : null);

    if (!targetVehicle) {
      setHistoryApiData(null);
      setLoadingHistory(false);
      return;
    }

    const vehicleId =
      targetVehicle.id ||
      targetVehicle.vehicle_id ||
      targetVehicle.raw?.id ||
      targetVehicle.raw?.vehicle_id;

    if (!vehicleId) {
      setHistoryApiData(null);
      return;
    }

    const { from, to } = getHistoryDateRange(historyPeriod, customRange);

    let isMounted = true;
    setLoadingHistory(true);
    setHistoryError(null);

    Promise.allSettled([
      apiService.getFmcrVehicleHistory({ vehicle_id: vehicleId, from, to }),
      apiService.getFmcrVehicleJourneySegments({ vehicle_id: vehicleId, from, to }),
    ])
      .then(([histRes, segRes]) => {
        if (!isMounted) return;
        const histVal = histRes.status === 'fulfilled' ? histRes.value : null;
        const segVal = segRes.status === 'fulfilled' ? segRes.value : null;

        if (histVal || segVal) {
          const res = histVal || { data: { points: [], polyline: [], vehicle_id: vehicleId } };
          const mapped = mapApiVehicleHistoryToHistoryData(res, targetVehicle);

          // If backend fn_get_vehicle_journey_segments returned segments, enrich mapped.segs
          if (segVal) {
            const rawSegs = segVal?.data || segVal;
            const segRows = Array.isArray(rawSegs?.segments)
              ? rawSegs.segments
              : Array.isArray(rawSegs?.rows)
              ? rawSegs.rows
              : Array.isArray(rawSegs)
              ? rawSegs
              : [];
            if (segRows.length > 0) {
              const pts = mapped.H?.pts || [];
              const backendSegs = segRows.map((r, sIdx) => {
                const sType = String(r.segment_type || r.type || 'STOPPED').toLowerCase();
                const st = sType === 'moving' ? 'moving' : sType === 'idle' ? 'idle' : 'stopped';
                const t0 = r.start_time ? new Date(r.start_time).getTime() : Date.now();
                const t1 = r.end_time ? new Date(r.end_time).getTime() : Date.now();
                const durSec = Number(r.duration_seconds || 0);
                const min = durSec > 0 ? durSec / 60 : Math.max(1, (t1 - t0) / 60000);
                const km = Number(r.distance_km || 0);
                const avg = Number(r.avg_speed_kmh || 0);
                const max = Number(r.max_speed_kmh || 0);

                // Coordinates from backend are [lat, lng], convert to GeoJSON [lng, lat]
                const p0 =
                  Array.isArray(r.start_coordinates) && r.start_coordinates.length >= 2
                    ? [Number(r.start_coordinates[1]), Number(r.start_coordinates[0])]
                    : [Number(r.start_lng || 0), Number(r.start_lat || 0)];
                const pEnd =
                  Array.isArray(r.end_coordinates) && r.end_coordinates.length >= 2
                    ? [Number(r.end_coordinates[1]), Number(r.end_coordinates[0])]
                    : [Number(r.end_lng || 0), Number(r.end_lat || 0)];

                let n0 = 0;
                let nEnd = pts.length > 0 ? pts.length - 1 : 0;
                if (pts.length > 0) {
                  let minDiff0 = Infinity;
                  let minDiffEnd = Infinity;
                  pts.forEach((pt, pIdx) => {
                    const diff0 = Math.abs(pt.t - t0);
                    const diffEnd = Math.abs(pt.t - t1);
                    if (diff0 < minDiff0) {
                      minDiff0 = diff0;
                      n0 = pIdx;
                    }
                    if (diffEnd < minDiffEnd) {
                      minDiffEnd = diffEnd;
                      nEnd = pIdx;
                    }
                  });
                }

                return {
                  st,
                  t0,
                  t1,
                  min,
                  km,
                  avg,
                  max,
                  fuel: Number(r.fuel_consumed_litres || r.est_utilized_fuel || 0),
                  p0,
                  pEnd,
                  locationStart: r.start_location || '',
                  locationEnd: r.end_location || '',
                  n0,
                  nEnd,
                  tripId: r.trip_id,
                  tripNo: r.trip_no,
                  isTrip: Boolean(r.is_trip),
                };
              });

              if (backendSegs.length > 0) {
                mapped.segs = backendSegs;
                mapped.stops = backendSegs.filter((g) => g.st === 'stopped').length;

                // If history API returned 0 raw telemetry points, synthesize trajectory from segments
                if ((!mapped.H?.pts || mapped.H.pts.length === 0) && backendSegs.length > 0) {
                  const synthPts = [];
                  backendSegs.forEach((seg, sIdx) => {
                    if (seg.p0 && (seg.p0[0] !== 0 || seg.p0[1] !== 0)) {
                      synthPts.push({
                        id: `seg-s-${sIdx}`,
                        p: seg.p0,
                        lat: seg.p0[1],
                        lng: seg.p0[0],
                        spd: seg.avg || 0,
                        h: 0,
                        ign: seg.st !== 'stopped',
                        t: seg.t0,
                        time: '',
                        location: seg.locationStart || '',
                      });
                    }
                    if (seg.pEnd && (seg.pEnd[0] !== 0 || seg.pEnd[1] !== 0)) {
                      synthPts.push({
                        id: `seg-e-${sIdx}`,
                        p: seg.pEnd,
                        lat: seg.pEnd[1],
                        lng: seg.pEnd[0],
                        spd: 0,
                        h: 0,
                        ign: seg.st === 'idle',
                        t: seg.t1,
                        time: '',
                        location: seg.locationEnd || '',
                      });
                    }
                  });
                  if (synthPts.length > 0) {
                    mapped.H = {
                      ...mapped.H,
                      pts: synthPts,
                      polylineCoords: synthPts.map((q) => q.p),
                    };
                  }
                }
              }

              // Enrich summary metrics from backend telemetry calculation
              if (rawSegs?.telemetry) {
                const tel = rawSegs.telemetry;
                if (tel.total_km != null && (mapped.km === 0 || tel.total_km > mapped.km)) {
                  mapped.km = Number(tel.total_km);
                }
                if (tel.driving_seconds != null) mapped.mv = Math.round(tel.driving_seconds / 60);
                if (tel.idle_seconds != null) mapped.idle = Math.round(tel.idle_seconds / 60);
                if (tel.stopped_seconds != null) mapped.stp = Math.round(tel.stopped_seconds / 60);
                if (tel.total_fuel_consumed_litres != null) mapped.fuel = Number(tel.total_fuel_consumed_litres);
                if (Array.isArray(tel.trips_summary) && tel.trips_summary.length > 0) {
                  mapped.trips = tel.trips_summary;
                }
                mapped.summary = {
                  ...mapped.summary,
                  total_km: mapped.km,
                  driving_time_formatted: tel.total_driving_formatted || mapped.summary.driving_time_formatted,
                  idle_time_formatted: tel.total_idle_formatted || mapped.summary.idle_time_formatted,
                  stopped_time_formatted: tel.total_stopped_formatted || mapped.summary.stopped_time_formatted,
                  total_time_formatted: tel.total_duration_formatted || mapped.summary.total_time_formatted,
                  total_fuel_consumed_litres: mapped.fuel,
                };
              }
            }
          }

          setHistoryApiData(mapped);
          setPlaybackStep(0);
          setIsPlaying(false);
        } else {
          throw histRes.reason || segRes.reason || new Error('Failed to load vehicle history');
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Failed to fetch vehicle history API:', err);
        setHistoryError(
          err?.message || (typeof err === 'string' ? err : 'Failed to fetch vehicle history')
        );
        setHistoryApiData(null);
      })
      .finally(() => {
        if (isMounted) setLoadingHistory(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    focusedReg,
    focusedVehicleObj?.id,
    historyPeriod,
    customRange.from,
    customRange.to,
    historyReloadTrigger,
  ]);

  const focusedHistoryData = useMemo(() => {
    return historyApiData;
  }, [historyApiData]);

  // Live telemetry and vehicle detail cache for real routes & specs
  const [telemetryCache, setTelemetryCache] = useState({});

  // Fetch telemetry and detail APIs directly
  const fetchTelemetryAndDetail = useCallback((vehicleId) => {
    if (!vehicleId) return;
    Promise.allSettled([
      apiService.getFmcrVehicleTrackingCard(vehicleId),
      apiService.getFmcrVehicleDetail(vehicleId),
    ]).then(([teleRes, detRes]) => {
      const tele = teleRes.status === 'fulfilled' ? (teleRes.value?.data || teleRes.value) : null;
      const det = detRes.status === 'fulfilled' ? (detRes.value?.data || detRes.value) : null;
      if (tele || det) {
        setTelemetryCache((prev) => ({
          ...prev,
          [vehicleId]: { tele, det },
        }));

        if (tele?.current_status) {
          setVehicles((prev) =>
            prev.map((v) =>
              v.id === vehicleId
                ? mergeVehicleWithViewData(v, {
                    ...v.raw,
                    vehicle_current_detail: tele.current_status,
                    last_log: tele.current_status,
                    polyline: tele.polyline,
                  })
                : v
            )
          );
        }
      }
    });
  }, []);

  // Prefetch telemetry & detail when selectedReg or focusedReg changes
  const activeRegForTelemetry = mode === 'history' ? (focusedReg || selectedReg) : selectedReg;
  useEffect(() => {
    if (!activeRegForTelemetry) return;
    const v = vehicles.find((veh) => veh.reg === activeRegForTelemetry);
    if (!v?.id || telemetryCache[v.id]) return;
    fetchTelemetryAndDetail(v.id);
  }, [activeRegForTelemetry, vehicles, telemetryCache, fetchTelemetryAndDetail]);

  // Selected vehicle for Live mode (enriched with live telemetry & detail cache)
  const selectedVehicle = useMemo(() => {
    if (!selectedReg) return null;
    const base = vehicles.find((v) => v.reg === selectedReg) || null;
    if (!base) return null;
    const cached = telemetryCache[base.id];
    if (!cached) return base;

    const { tele, det } = cached;
    
    // Resolve polyline from all possible sources (trip detail, full_polyline, polyline_traveled, tele, base)
    const tripObj = det?.fmcr_trip || base.trip || null;
    const polyObj = tripObj?.polyline || det?.polyline || tele?.polyline || base.polylineGeoJson || null;
    const encPoly =
      (typeof polyObj === 'string' ? polyObj : polyObj?.full_polyline || polyObj?.polyline) ||
      tripObj?.full_polyline ||
      tripObj?.polyline_traveled ||
      (typeof tele?.polyline === 'string' ? tele.polyline : '') ||
      '';

    let polylineGeoJson = null;
    if (encPoly && typeof encPoly === 'string') {
      const decoded = decodePolyline(encPoly);
      if (decoded.length >= 2) polylineGeoJson = decoded;
    } else if (Array.isArray(polyObj) && polyObj.length >= 2) {
      polylineGeoJson = polyObj
        .filter((pt) => Array.isArray(pt) && pt.length >= 2)
        .map(([p0, p1]) => {
          const num0 = Number(p0);
          const num1 = Number(p1);
          if (num0 > 55 && num1 < 45) return [num0, num1]; // [lng, lat]
          if (num1 > 55 && num0 < 45) return [num1, num0]; // [lat, lng] -> [lng, lat]
          return [num1, num0];
        });
    } else if (base.polylineGeoJson && base.polylineGeoJson.length >= 2) {
      polylineGeoJson = base.polylineGeoJson;
    }

    const curStatus = tele?.current_status;
    const curLat =
      curStatus?.lat != null && !isNaN(Number(curStatus.lat)) && Number(curStatus.lat) !== 0
        ? Number(curStatus.lat)
        : base.lat;
    const curLng =
      curStatus?.lng != null && !isNaN(Number(curStatus.lng)) && Number(curStatus.lng) !== 0
        ? Number(curStatus.lng)
        : base.lng;
    const curLoc = curStatus?.location || base.loc;
    const curSpd = curStatus?.speed != null ? Number(curStatus.speed) : base.spd;
    const curIgn = curStatus?.ignition != null ? Boolean(curStatus.ignition) : base.ignition;

    const teleLastSeen = curStatus?.last_seen_at ? new Date(curStatus.last_seen_at).getTime() : 0;
    const vehLastSeen = base.last ? Number(base.last) : 0;
    const isActivelyMoving = (curSpd > 0 || base.st === 'moving');
    const curLast = isActivelyMoving
      ? Math.max(teleLastSeen, vehLastSeen, Date.now() - 30000)
      : (Math.max(teleLastSeen, vehLastSeen) || Date.now());

    // Resolve heading / direction
    let curH =
      curStatus?.angle ??
      curStatus?.heading ??
      curStatus?.bearing ??
      base.h ??
      0;

    if (curH === 0) {
      if (polylineGeoJson && polylineGeoJson.length >= 2) {
        curH = getRouteHeading(curLat && curLng ? [curLng, curLat] : null, polylineGeoJson);
      } else {
        const stObj = tripObj?.start_trip || (Array.isArray(tripObj?.stops) && tripObj.stops[0]) || null;
        const endObj = tripObj?.end_trip || (Array.isArray(tripObj?.stops) && tripObj.stops[tripObj.stops.length - 1]) || null;
        if (stObj && endObj) {
          const sLng = Number(stObj.longitude || stObj.lng);
          const sLat = Number(stObj.latitude || stObj.lat);
          const eLng = Number(endObj.longitude || endObj.lng);
          const eLat = Number(endObj.latitude || endObj.lat);
          if (!isNaN(sLng) && !isNaN(sLat) && !isNaN(eLng) && !isNaN(eLat) && sLng !== 0 && eLng !== 0) {
            curH = computeBearing([sLng, sLat], [eLng, eLat]);
          }
        }
      }
    }

    return {
      ...base,
      lat: curLat,
      lng: curLng,
      loc: curLoc,
      spd: curSpd,
      h: Math.round(curH),
      ignition: curIgn,
      last: curLast,
      telemetryData: tele,
      detailData: det,
      polyline: polyObj,
      polylineGeoJson,
      route: polylineGeoJson,
      stops: det?.fmcr_trip?.stops || base.stops || [],
      start_trip: det?.fmcr_trip?.start_trip || base.start_trip,
      end_trip: det?.fmcr_trip?.end_trip || base.end_trip,
      trip: det?.fmcr_trip || base.trip,
    };
  }, [vehicles, selectedReg, telemetryCache]);

  // Synchronize fleet vehicles immediately to Redux on every update
  useEffect(() => {
    if (vehicles.length > 0) {
      dispatch(setTrackingVehicles(vehicles));
    }
  }, [vehicles, dispatch]);

  // Synchronize selected vehicle immediately to Redux
  useEffect(() => {
    if (selectedVehicle) {
      dispatch(setSelectedTrackingVehicle(selectedVehicle));
    } else {
      dispatch(clearSelectedVehicle());
    }
  }, [selectedVehicle, dispatch]);

  // Active selected vehicle for live socket tracking
  const activeVehicleForSocket = mode === 'history' ? (focusedVehicleObj || selectedVehicle) : selectedVehicle;
  const selectedImei = activeVehicleForSocket?.imeino || activeVehicleForSocket?.imei || activeVehicleForSocket?.dev || '';

  // =========================================================================
  // SOCKET INTEGRATIONS: ALL 6 CHANNELS AS SPECIFIED
  // 1. vehicle-location-${vehicle_id} (Primary Vehicle ID) -> Live Map / Tracking
  // 1b. vehicle-location-${imeino}    (Fallback IMEI)      -> Live Map / Tracking
  // 2. vehicle-violation-alert        (Global Fleet-wide)  -> Control Room Fleet-Wide Violations
  // 3. trip-violation-${trip_id}       (Per Trip)           -> Trip Details / Timeline Tracking
  // 4. vehicle-violation-${vehicle_id} (Per Vehicle ID)     -> Vehicle Profile / Single Vehicle
  // 5. new-logs-${trip_id}             (Per Trip)           -> Trip Logs Auto-Refresh
  // 6. test                           (Static)             -> Socket Health Check
  // =========================================================================

  // Global socket channels: Event 2 (vehicle-violation-alert) and Event 6 (test)
  useEffect(() => {
    if (!activeSockets.length) return;

    // 6. Static Socket Health Check: 'test'
    const handleSocketTest = (data) => {
      console.log('📡 [SOCKET HEALTH TEST] Ping received:', data);
      dispatch(
        setSocketHealthStatus({
          isConnected: true,
          lastPing: Date.now(),
          message: typeof data === 'string' ? data : 'Socket healthy',
          data,
        })
      );
    };

    // 2. Global Fleet Violation Alert: 'vehicle-violation-alert'
    const handleGlobalViolationAlert = (data) => {
      if (!data) return;
      console.log('🚨 [FLEET VIOLATION ALERT] Broadcast received:', data);
      const norm = normalizeViolation(data);
      if (!norm) return;

      // Update Redux global state & violation counters
      dispatch(addGlobalViolationAlert(norm));

      // Live update matching vehicle in local state so summary strip "Violations today" pill and list update instantly
      setVehicles((prev) =>
        prev.map((v) => {
          const isMatch =
            (norm.vehicle_id && Number(v.id) === Number(norm.vehicle_id)) ||
            (norm.reg && String(v.reg).toLowerCase() === String(norm.reg).toLowerCase()) ||
            (norm.imeino &&
              (String(v.imeino).trim() === norm.imeino ||
                String(v.imei).trim() === norm.imeino ||
                String(v.dev).trim() === norm.imeino));

          if (!isMatch) return v;
          return {
            ...v,
            vio: [norm, ...(v.vio || [])],
          };
        })
      );
    };

    activeSockets.forEach((s) => {
      s.on('test', handleSocketTest);
      s.on('vehicle-violation-alert', handleGlobalViolationAlert);
    });

    return () => {
      activeSockets.forEach((s) => {
        s.off('test', handleSocketTest);
        s.off('vehicle-violation-alert', handleGlobalViolationAlert);
      });
    };
  }, [activeSockets, dispatch]);

  // 1 & 1b. Fleet-wide Live Map Location Sockets: vehicle-location-${vehicle_id} (Primary) & vehicle-location-${imeino} (Fallback)
  useEffect(() => {
    if (!activeSockets.length || vehicles.length === 0) return;

    const handleFleetLocationUpdate = (data) => {
      if (!data) return;

      let newLat = data.lat != null && !isNaN(Number(data.lat)) ? Number(data.lat) : undefined;
      let newLng = data.lng != null && !isNaN(Number(data.lng)) ? Number(data.lng) : undefined;

      // Coordinate sanity check: swap if reversed
      if (newLat !== undefined && newLng !== undefined && newLat > 55 && newLng < 45) {
        const swap = newLat;
        newLat = newLng;
        newLng = swap;
      }

      const newSpd = data.speed != null && !isNaN(Number(data.speed)) ? Number(data.speed) : undefined;
      const location = data.location || undefined;
      const ignition = data.ignition != null ? Boolean(data.ignition) : newSpd != null ? newSpd > 0 : undefined;
      const trackerStatus = normalizeStatus(
        data.tracker_status || data.status,
        newSpd,
        ignition
      );
      const updatedAt =
        data.created_at ||
        data.last_updated ||
        data.updated_at ||
        data.timestamp ||
        new Date().toISOString();

      const rawImei = data.imeino || data.imei || data.dev || '';
      const cleanImei = rawImei ? String(rawImei).trim() : '';
      const vehicleId = data.vehicle_id || data.id;

      const rawH = data.bearing ?? data.heading ?? data.angle ?? data.course ?? data.direction ?? data.h;
      const parsedH = rawH != null && !isNaN(Number(rawH)) ? Number(rawH) : null;

      // Update Redux
      dispatch(
        updateVehicleLocationFromSocket({
          ...data,
          imeino: cleanImei,
          vehicle_id: vehicleId,
          lat: newLat,
          lng: newLng,
          speed: newSpd,
          heading: parsedH,
          location,
          ignition,
          status: trackerStatus,
          updatedAt,
        })
      );

      // Save socket position to persistent cache
      if (newLat !== undefined && newLng !== undefined) {
        saveCachedVehicleCoords([
          {
            id: vehicleId,
            imeino: cleanImei,
            lat: newLat,
            lng: newLng,
            h: parsedH,
            spd: newSpd,
            st: trackerStatus ? trackerStatus.toLowerCase() : undefined,
            loc: location,
          },
        ]);
      }

      // Update matching vehicle in local state
      setVehicles((prev) =>
        prev.map((v) => {
          const isMatch =
            (vehicleId && (Number(v.id) === Number(vehicleId) || Number(v.vehicle_id) === Number(vehicleId))) ||
            (cleanImei && (String(v.imeino).trim() === cleanImei || String(v.imei).trim() === cleanImei));

          if (!isMatch) return v;

          let polylineGeoJson = v.polylineGeoJson || [];
          if (newLat !== undefined && newLng !== undefined && newLat !== 0 && newLng !== 0) {
            const lastPt = polylineGeoJson[polylineGeoJson.length - 1];
            if (!lastPt || lastPt[0] !== newLng || lastPt[1] !== newLat) {
              polylineGeoJson = [...polylineGeoJson, [newLng, newLat]];
            }
          }

          let vehicleH = v.h || 0;
          if (parsedH != null && parsedH !== 0) {
            vehicleH = parsedH;
          } else if (
            newLat !== undefined &&
            newLng !== undefined &&
            v.lat &&
            v.lng &&
            (newLat !== v.lat || newLng !== v.lng)
          ) {
            vehicleH = computeBearing([v.lng, v.lat], [newLng, newLat]);
          } else if (polylineGeoJson.length >= 2) {
            vehicleH = computeBearing(
              polylineGeoJson[polylineGeoJson.length - 2],
              polylineGeoJson[polylineGeoJson.length - 1]
            );
          }

          return {
            ...v,
            ...(newLat !== undefined ? { lat: newLat } : {}),
            ...(newLng !== undefined ? { lng: newLng } : {}),
            ...(location && location !== '-' ? { loc: location } : {}),
            ...(newSpd !== undefined ? { spd: newSpd } : {}),
            ...(ignition !== undefined ? { ignition } : {}),
            st: trackerStatus,
            h: Math.round(vehicleH),
            last: new Date(updatedAt).getTime(),
            polylineGeoJson,
            route: polylineGeoJson,
          };
        })
      );

      // Keep telemetryCache fresh so if this vehicle is currently expanded/viewed, stats update
      if (vehicleId) {
        setTelemetryCache((prev) => {
          const existing = prev[vehicleId] || {};
          const existingTele = existing.tele || {};
          const existingCurStatus = existingTele.current_status || {};

          return {
            ...prev,
            [vehicleId]: {
              ...existing,
              tele: {
                ...existingTele,
                current_status: {
                  ...existingCurStatus,
                  ...(newLat !== undefined ? { lat: newLat } : {}),
                  ...(newLng !== undefined ? { lng: newLng } : {}),
                  ...(newSpd !== undefined ? { speed: newSpd } : {}),
                  ...(location ? { location } : {}),
                  ...(ignition !== undefined ? { ignition } : {}),
                  ...(parsedH != null ? { angle: parsedH, heading: parsedH } : {}),
                  last_seen_at: updatedAt,
                },
              },
            },
          };
        });
      }
    };

    const registeredEvents = new Set();
    vehicles.forEach((v) => {
      const vId = v.id || v.vehicle_id;
      const imei = v.imeino || v.imei || v.dev;
      if (vId) {
        const ev = `vehicle-location-${vId}`;
        if (!registeredEvents.has(ev)) {
          registeredEvents.add(ev);
          activeSockets.forEach((s) => s.on(ev, handleFleetLocationUpdate));
        }
      }
      if (imei && String(imei).trim() !== String(vId)) {
        const ev = `vehicle-location-${String(imei).trim()}`;
        if (!registeredEvents.has(ev)) {
          registeredEvents.add(ev);
          activeSockets.forEach((s) => s.on(ev, handleFleetLocationUpdate));
        }
      }
    });

    return () => {
      registeredEvents.forEach((ev) => {
        activeSockets.forEach((s) => s.off(ev, handleFleetLocationUpdate));
      });
    };
  }, [activeSockets, vehicles.length, dispatch]);

  // Dynamic Selected Vehicle & Trip Sockets: Events 1, 1b, 3, 4, 5
  useEffect(() => {
    if (!activeVehicleForSocket || !activeSockets.length) {
      dispatch(clearSelectedVehicle());
      return;
    }

    const cleanImei = selectedImei ? String(selectedImei).trim() : '';
    const vehicleId = activeVehicleForSocket.id;
    const tripId =
      activeVehicleForSocket.trip?.id ||
      activeVehicleForSocket.trip?.trip_id ||
      activeVehicleForSocket.detailData?.fmcr_trip?.id ||
      activeVehicleForSocket.telemetryData?.trip_id ||
      null;

    // Sync selected vehicle in Redux store
    dispatch(setSelectedTrackingVehicle(activeVehicleForSocket));

    // 1 & 1b. Dynamic Vehicle Location Ping:
    // Primary: vehicle-location-${vehicle_id}
    // Fallback: vehicle-location-${imeino}
    const primaryLocationEvent = vehicleId ? `vehicle-location-${vehicleId}` : null;
    const fallbackLocationEvent = cleanImei && String(cleanImei) !== String(vehicleId)
      ? `vehicle-location-${cleanImei}`
      : null;

    const handleLocationUpdate = (data) => {
      if (!data) return;

      let newLat = data.lat != null && !isNaN(Number(data.lat)) ? Number(data.lat) : undefined;
      let newLng = data.lng != null && !isNaN(Number(data.lng)) ? Number(data.lng) : undefined;

      // Coordinate sanity check: swap if reversed
      if (newLat !== undefined && newLng !== undefined && newLat > 55 && newLng < 45) {
        const swap = newLat;
        newLat = newLng;
        newLng = swap;
      }

      const newSpd = data.speed != null && !isNaN(Number(data.speed)) ? Number(data.speed) : undefined;
      const location = data.location || undefined;
      const ignition = data.ignition != null ? Boolean(data.ignition) : newSpd != null ? newSpd > 0 : undefined;
      const trackerStatus = normalizeStatus(
        data.tracker_status || data.status,
        newSpd,
        ignition
      );
      const updatedAt =
        data.created_at ||
        data.last_updated ||
        data.updated_at ||
        data.timestamp ||
        new Date().toISOString();

      const rawH = data.bearing ?? data.heading ?? data.angle ?? data.course ?? data.direction ?? data.h;
      const parsedH = rawH != null && !isNaN(Number(rawH)) ? Number(rawH) : null;

      // Dispatch live update to Redux
      dispatch(
        updateVehicleLocationFromSocket({
          ...data,
          imeino: cleanImei || data.imeino || data.imei,
          vehicle_id: vehicleId || data.vehicle_id,
          lat: newLat,
          lng: newLng,
          speed: newSpd,
          heading: parsedH,
          location,
          ignition,
          status: trackerStatus,
          updatedAt,
        })
      );

      // If trip data attached, also update Redux Trip slice
      if (data.trip_id || tripId) {
        dispatch(
          updateTripDetailsLiveData({
            trip_id: data.trip_id || tripId,
            lat: newLat,
            lng: newLng,
            speed: newSpd,
          })
        );
      }

      let currentHeading = activeVehicleForSocket.h || 0;

      // Update local vehicles state so map marker, camera, and list update instantly
      setVehicles((prev) =>
        prev.map((v) => {
          const isMatch =
            (vehicleId && (Number(v.id) === Number(vehicleId) || Number(v.vehicle_id) === Number(vehicleId))) ||
            (data.vehicle_id && (Number(v.id) === Number(data.vehicle_id) || Number(v.vehicle_id) === Number(data.vehicle_id))) ||
            (cleanImei && (String(v.imeino).trim() === cleanImei || String(v.imei).trim() === cleanImei)) ||
            (v.reg && activeVehicleForSocket.reg && v.reg === activeVehicleForSocket.reg);

          if (!isMatch) return v;

          let polylineGeoJson = v.polylineGeoJson || [];
          if (newLat !== undefined && newLng !== undefined && newLat !== 0 && newLng !== 0) {
            const lastPt = polylineGeoJson[polylineGeoJson.length - 1];
            if (!lastPt || lastPt[0] !== newLng || lastPt[1] !== newLat) {
              polylineGeoJson = [...polylineGeoJson, [newLng, newLat]];
            }
          }

          let vehicleH = v.h || 0;
          if (parsedH != null && parsedH !== 0) {
            vehicleH = parsedH;
          } else if (
            newLat !== undefined &&
            newLng !== undefined &&
            v.lat &&
            v.lng &&
            (newLat !== v.lat || newLng !== v.lng)
          ) {
            vehicleH = computeBearing([v.lng, v.lat], [newLng, newLat]);
          } else if (polylineGeoJson.length >= 2) {
            vehicleH = computeBearing(
              polylineGeoJson[polylineGeoJson.length - 2],
              polylineGeoJson[polylineGeoJson.length - 1]
            );
          }
          currentHeading = vehicleH;

          return {
            ...v,
            ...(newLat !== undefined ? { lat: newLat } : {}),
            ...(newLng !== undefined ? { lng: newLng } : {}),
            ...(location && location !== '-' ? { loc: location } : {}),
            ...(newSpd !== undefined ? { spd: newSpd } : {}),
            ...(ignition !== undefined ? { ignition } : {}),
            st: trackerStatus,
            h: Math.round(vehicleH),
            last: new Date(updatedAt).getTime(),
            polylineGeoJson,
            route: polylineGeoJson,
          };
        })
      );

      // Update telemetryCache for instant Card update
      if (vehicleId) {
        setTelemetryCache((prev) => {
          const existing = prev[vehicleId] || {};
          const existingTele = existing.tele || {};
          const existingCurStatus = existingTele.current_status || {};

          return {
            ...prev,
            [vehicleId]: {
              ...existing,
              tele: {
                ...existingTele,
                current_status: {
                  ...existingCurStatus,
                  ...(newLat !== undefined ? { lat: newLat } : {}),
                  ...(newLng !== undefined ? { lng: newLng } : {}),
                  ...(newSpd !== undefined ? { speed: newSpd } : {}),
                  ...(location ? { location } : {}),
                  ...(ignition !== undefined ? { ignition } : {}),
                  ...(currentHeading ? { angle: currentHeading, heading: currentHeading } : {}),
                  last_seen_at: updatedAt,
                },
              },
            },
          };
        });
      }
    };

    // 4. Dynamic Single Vehicle Violation: vehicle-violation-${vehicle_id}
    const vehicleViolationEvent = vehicleId ? `vehicle-violation-${vehicleId}` : null;
    const handleVehicleViolation = (data) => {
      if (!data) return;
      console.log(`⚠️ [VEHICLE VIOLATION] Alert received for vehicle ${vehicleId}:`, data);
      const norm = normalizeViolation({ ...data, vehicle_id: vehicleId });
      if (!norm) return;

      dispatch(addVehicleViolationAlert({ vehicle_id: vehicleId, violation: norm }));

      setVehicles((prev) =>
        prev.map((v) =>
          v.id === vehicleId
            ? { ...v, vio: [norm, ...(v.vio || [])] }
            : v
        )
      );
    };

    // 3. Dynamic Trip Violation: trip-violation-${trip_id}
    const tripViolationEvent = tripId ? `trip-violation-${tripId}` : null;
    const handleTripViolation = (data) => {
      if (!data) return;
      console.log(`⚠️ [TRIP VIOLATION] Alert received for trip ${tripId}:`, data);
      const norm = normalizeViolation({ ...data, trip_id: tripId, vehicle_id: vehicleId });
      if (!norm) return;

      dispatch(addTripViolationAlert({ trip_id: tripId, violation: norm }));

      if (vehicleId) {
        setVehicles((prev) =>
          prev.map((v) =>
            v.id === vehicleId
              ? { ...v, vio: [norm, ...(v.vio || [])] }
              : v
          )
        );
      }
    };

    // 5. Dynamic Trip Logs Refresh Signal: new-logs-${trip_id}
    const newLogsEvent = tripId ? `new-logs-${tripId}` : null;
    const handleNewLogs = (data) => {
      console.log(`🔄 [NEW LOGS REFRESH] Signal received for trip ${tripId}:`, data);
      dispatch(setTripNewLogsSignal({ trip_id: tripId, timestamp: Date.now(), data }));

      // Immediately pull fresh telemetry, polyline coordinates & logs from API
      if (vehicleId) {
        fetchTelemetryAndDetail(vehicleId);
      }
    };

    // Bind listeners to all active sockets
    activeSockets.forEach((s) => {
      if (primaryLocationEvent) s.on(primaryLocationEvent, handleLocationUpdate);
      if (fallbackLocationEvent) s.on(fallbackLocationEvent, handleLocationUpdate);
      if (vehicleViolationEvent) s.on(vehicleViolationEvent, handleVehicleViolation);
      if (tripViolationEvent) s.on(tripViolationEvent, handleTripViolation);
      if (newLogsEvent) s.on(newLogsEvent, handleNewLogs);
    });

    // Teardown
    return () => {
      activeSockets.forEach((s) => {
        if (primaryLocationEvent) s.off(primaryLocationEvent, handleLocationUpdate);
        if (fallbackLocationEvent) s.off(fallbackLocationEvent, handleLocationUpdate);
        if (vehicleViolationEvent) s.off(vehicleViolationEvent, handleVehicleViolation);
        if (tripViolationEvent) s.off(tripViolationEvent, handleTripViolation);
        if (newLogsEvent) s.off(newLogsEvent, handleNewLogs);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeVehicleForSocket?.id,
    activeVehicleForSocket?.reg,
    selectedImei,
    activeSockets,
    dispatch,
    fetchTelemetryAndDetail,
  ]);

  // Live status counts
  const statusCounts = useMemo(() => {
    const counts = { all: vehicles.length, moving: 0, idle: 0, stopped: 0, inactive: 0 };
    vehicles.forEach((v) => {
      const i = info(v);
      if (counts[i.status] !== undefined) counts[i.status]++;
    });
    return counts;
  }, [vehicles]);

  // Playback timer loop
  useEffect(() => {
    let interval = null;
    if (isPlaying && focusedReg && focusedHistoryData?.H?.pts?.length) {
      interval = setInterval(() => {
        setPlaybackStep((prev) => {
          const maxStep = focusedHistoryData.H.pts.length - 1;
          if (prev >= maxStep) {
            setIsPlaying(false);
            return maxStep;
          }
          return prev + 1;
        });
      }, 400 / playbackSpeed);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, focusedReg, focusedHistoryData, playbackSpeed]);

  // Handlers
  const handleSelectLiveVehicle = (v) => {
    if (!v) {
      setSelectedReg(null);
      setExpandedReg(null);
      setFollowVehicle(false);
      setFlyTrigger(Date.now());
      dispatch(clearSelectedVehicle());
      return;
    }
    if (selectedReg === v.reg && expandedReg === v.reg) {
      setSelectedReg(null);
      setExpandedReg(null);
      setFollowVehicle(false);
      setFlyTrigger(Date.now());
      dispatch(clearSelectedVehicle());
    } else {
      setSelectedReg(v.reg);
      setExpandedReg(v.reg);
      setFollowVehicle(true);
      setFlyTrigger(Date.now());
      dispatch(setSelectedTrackingVehicle(v));
    }
  };

  const handleRowClickLive = (v) => {
    if (expandedReg === v.reg) {
      setExpandedReg(null);
      setSelectedReg(null);
      setFollowVehicle(false);
      setFlyTrigger(Date.now());
      dispatch(clearSelectedVehicle());
    } else {
      setSelectedReg(v.reg);
      setExpandedReg(v.reg);
      setFollowVehicle(true);
      setFlyTrigger(Date.now());
      dispatch(setSelectedTrackingVehicle(v));
    }
  };

  const handleShowTripOnMap = (reg) => {
    setSelectedReg(reg);
    setExpandedReg(reg);
    setFollowVehicle(false);
    setFlyTrigger(Date.now());
    setTriggerFitTrip(Date.now());
    const match = vehicles.find((v) => v.reg === reg);
    if (match) dispatch(setSelectedTrackingVehicle(match));
  };

  const handleFocusHistoryVehicle = (regOrVeh) => {
    const reg = typeof regOrVeh === 'object' && regOrVeh !== null ? regOrVeh.reg : regOrVeh;
    if (!reg) return;
    setFocusedReg(reg);
    setSelectedReg(reg);
    setPlaybackStep(0);
    setIsPlaying(false);
    const v = vehicles.find((veh) => veh.reg === reg);
    if (v?.id) {
      fetchTelemetryAndDetail(v.id);
      dispatch(setSelectedTrackingVehicle(v));
    }
  };

  const handleBackToFleetHistory = () => {
    setFocusedReg(null);
    setPlaybackStep(0);
    setIsPlaying(false);
  };

  const handleLiveListScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 250) {
        setVisibleLiveCount((prev) => Math.min(prev + 15, filteredVehicles.length));
      }
    },
    [filteredVehicles.length]
  );

  useEffect(() => {
    setVisibleLiveCount(20);
  }, [statusFilter, filters]);

  useEffect(() => {
    if (selectedReg && filteredVehicles.length > 0) {
      const selIdx = filteredVehicles.findIndex((v) => v.reg === selectedReg);
      if (selIdx >= visibleLiveCount) {
        setVisibleLiveCount(Math.min(filteredVehicles.length, selIdx + 10));
      }
    }
  }, [selectedReg, filteredVehicles, visibleLiveCount]);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleOutsideClick = (e) => {
      if (exportAnchorEl && !exportAnchorEl.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [exportMenuOpen, exportAnchorEl]);

  const triggerPdfDownload = (fileUrl, filename) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    const absoluteUrl = fileUrl.startsWith('http')
      ? fileUrl
      : `${baseUrl.replace(/\/api\/?$/, '')}/${fileUrl}`;
    const link = document.createElement('a');
    link.href = absoluteUrl;
    link.target = '_blank';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportTopPdf = async () => {
    const { from: todayFrom, to: todayTo } = getHistoryDateRange('today');

    // History mode — single focused vehicle: per-vehicle journey PDF
    if (mode === 'history' && focusedReg) {
      const vObj = vehicles.find((v) => v.reg === focusedReg);
      if (vObj?.id) {
        try {
          setExportingTopPdf(true);
          const { from, to } = getHistoryDateRange(historyPeriod, customRange);
          const res = await apiService.exportFmcrVehicleJourneySegmentsPdf({ vehicle_id: vObj.id, from, to });
          const fileUrl = res?.data?.file_url || res?.file_url;
          if (fileUrl) triggerPdfDownload(fileUrl, `vehicle-${vObj.reg}-history.pdf`);
          else alert('PDF URL not returned from server.');
        } catch (err) {
          alert(typeof err === 'string' ? err : 'Failed to export history PDF');
        } finally {
          setExportingTopPdf(false);
          setExportMenuOpen(false);
        }
        return;
      }
    }

    // History mode — no vehicle focused: fleet history summary PDF for all scope vehicles
    if (mode === 'history') {
      const scopeIds = historyScopeVehicles.map((v) => v.id).filter(Boolean);
      if (!scopeIds.length) { alert('No vehicles available to export.'); return; }
      try {
        setExportingTopPdf(true);
        const { from, to } = getHistoryDateRange(historyPeriod, customRange);
        const res = await apiService.exportFmcrVehicleHistorySummaryPdf({ vehicle_ids: scopeIds, from, to });
        const fileUrl = res?.data?.file_url || res?.file_url;
        if (fileUrl) triggerPdfDownload(fileUrl, `fleet-history-summary.pdf`);
        else alert('PDF URL not returned from server.');
      } catch (err) {
        alert(typeof err === 'string' ? err : 'Failed to export fleet history PDF');
      } finally {
        setExportingTopPdf(false);
        setExportMenuOpen(false);
      }
      return;
    }

    // Live mode — specific vehicle selected: per-vehicle journey PDF (today)
    const targetVehicle =
      selectedVehicle || (selectedReg ? vehicles.find((v) => v.reg === selectedReg) : null);
    if (targetVehicle?.id) {
      try {
        setExportingTopPdf(true);
        const res = await apiService.exportFmcrVehicleJourneySegmentsPdf({
          vehicle_id: targetVehicle.id,
          from: todayFrom,
          to: todayTo,
        });
        const fileUrl = res?.data?.file_url || res?.file_url;
        if (fileUrl) triggerPdfDownload(fileUrl, `vehicle-${targetVehicle.reg || targetVehicle.id}-journey.pdf`);
        else alert('PDF URL not returned from server.');
      } catch (err) {
        alert(typeof err === 'string' ? err : 'Failed to export vehicle PDF');
      } finally {
        setExportingTopPdf(false);
        setExportMenuOpen(false);
      }
      return;
    }

    // Live mode — no vehicle selected: live snapshot PDF for all filtered vehicles
    const allIds = filteredVehicles.map((v) => v.id).filter(Boolean);
    if (!allIds.length) { alert('No vehicles available to export.'); return; }
    try {
      setExportingTopPdf(true);
      const res = await apiService.exportFmcrVehiclesLiveSnapshotPdf({ vehicle_Ids: allIds });
      const fileUrl = res?.data?.file_url || res?.file_url;
      if (fileUrl) triggerPdfDownload(fileUrl, `fleet-live-snapshot.pdf`);
      else alert('PDF URL not returned from server.');
    } catch (err) {
      alert(typeof err === 'string' ? err : 'Failed to export fleet PDF');
    } finally {
      setExportingTopPdf(false);
      setExportMenuOpen(false);
    }
  };

  const handleExportCSV = () => {
    const listToExport = mode === 'live' ? filteredVehicles : historyScopeVehicles;
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      ['Registration,Driver,Category,Type,Status,Speed,Location,IMEI,KM Today']
        .concat(
          listToExport.map(
            (v) =>
              `${v.reg},${v.drv},${v.cat},${v.type},${v.st},${v.spd},"${info(v).loc}",${v.dev},${Math.round(
                v.km || 0
              )}`
          )
        )
        .join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `vehicle_tracking_${mode}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length + (statusFilter !== 'all' ? 1 : 0);

  const handleOpenFilterPop = (e) => {
    setFilterAnchorEl(e.currentTarget);
    setFilterPopOpen(true);
  };

  const handleOpenPickerPop = (e) => {
    setPickerAnchorEl(e.currentTarget);
    setPickerPopOpen(true);
  };

  const pickerBtnLabel = () => {
    if (!selectedVehicleRegs.length) return 'All vehicles';
    if (selectedVehicleRegs.length === 1) return selectedVehicleRegs[0];
    return `${selectedVehicleRegs.length} vehicles`;
  };

  // Animate status count chips briefly when counts change
  const prevStatusCounts = useRef(statusCounts);
  useEffect(() => {
    prevStatusCounts.current = statusCounts;
  });

  return (
    <div className={`vehicle-tracking-module ${mapOnly ? 'map-only' : ''}`}>
      <div
        className={`lt ${view === 'map' ? 'lt-mapv' : ''} ${
          mode === 'history' ? 'lt-hist' : ''
        } ${mapOnly ? 'map-only' : ''}`}
      >
          {/* Header */}
          <div className="lt-head">
            {mode === 'live' ? (
              // LIVE MODE HEADER
              <div className="lt-row lt-main">
                {/* Live / History Switcher */}
                <div className="segbar sm lt-mode" role="tablist">
                  <button
                    type="button"
                    className={mode === 'live' ? 'on' : ''}
                    onClick={() => setMode('live')}
                  >
                    <span className="live-dot" />
                    Live
                  </button>
                  <button
                    type="button"
                    className={mode === 'history' ? 'on' : ''}
                    onClick={() => {
                      setMode('history');
                      setFocusedReg(null);
                      setPlaybackStep(0);
                      setIsPlaying(false);
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: ic('history', 'width="14" height="14"') }} />
                    History
                  </button>
                </div>

                {/* Tracking / Listing Switcher */}
                <div className="tabs sm vtog" role="tablist">
                  <button
                    type="button"
                    className={view === 'map' ? 'on' : ''}
                    onClick={() => setView('map')}
                  >
                    Tracking
                  </button>
                  <button
                    type="button"
                    className={view === 'list' ? 'on' : ''}
                    onClick={() => setView('list')}
                  >
                    Listing
                  </button>
                </div>

                {/* Status Chips */}
                <div className="segbar sm" id="ltchips" role="group">
                  {['all', 'moving', 'idle', 'stopped', 'inactive'].map((stKey) => (
                    <button
                      key={stKey}
                      type="button"
                      className={statusFilter === stKey ? 'on' : ''}
                      onClick={() => setStatusFilter(stKey)}
                    >
                      {stKey !== 'all' && (
                        <span
                          className="sdot-s"
                          style={{ background: STATUS_HEX[stKey] }}
                        />
                      )}
                      {stKey === 'all' ? 'All' : STATUS_LABEL[stKey]}
                      <span className="n">{statusCounts[stKey]}</span>
                    </button>
                  ))}
                </div>

                {/* Filter Popover Trigger */}
                <button
                  ref={ltFilterBtnRef}
                  id="ltftog"
                  type="button"
                  className={`btn sm lt-ftog ${activeFiltersCount > 0 ? 'has' : ''}`}
                  onClick={handleOpenFilterPop}
                >
                  <span dangerouslySetInnerHTML={{ __html: ic('filter', 'width="14" height="14"') }} />
                  <span className="tl">Filters</span>
                  {activeFiltersCount > 0 && <span className="fbadge">{activeFiltersCount}</span>}
                </button>

                {/* Actions / Export */}
                <div className="lt-actions" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExportAnchorEl(e.currentTarget);
                      setExportMenuOpen((prev) => !prev);
                    }}
                    title="Export options (PDF / CSV)"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: ic('report') }} />
                    <span className="tl">Export</span>
                  </button>

                  {exportMenuOpen && (
                    <div
                      className="export-dropdown-menu"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '6px',
                        background: '#ffffff',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '220px',
                        padding: '6px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleExportTopPdf}
                        disabled={exportingTopPdf}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1E293B',
                          background: 'none',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: exportingTopPdf ? 'wait' : 'pointer',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F5F9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <span dangerouslySetInnerHTML={{ __html: ic('report', 'width="14" height="14" stroke="#DC2626"') }} />
                        {exportingTopPdf
                          ? 'Generating PDF...'
                          : selectedVehicle
                          ? `PDF: ${selectedVehicle.reg}`
                          : `PDF: All Vehicles (${filteredVehicles.length})`}
                      </button>

                      <div style={{ height: '1px', background: '#E2E8F0', margin: '4px 0' }} />

                      <button
                        type="button"
                        onClick={() => {
                          handleExportCSV();
                          setExportMenuOpen(false);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1E293B',
                          background: 'none',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F5F9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <span dangerouslySetInnerHTML={{ __html: ic('list', 'width="14" height="14" stroke="#16A34A"') }} />
                        Excel / CSV Dataset
                      </button>
                    </div>
                  )}
                </div>

                {/* Status Guide Icon */}
                <span className="tb-help">
                  <button
                    type="button"
                    className="info-tip"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHelpModal(true);
                    }}
                    title="Status guide"
                  >
                    i
                  </button>
                </span>
              </div>
            ) : (
              // HISTORY MODE HEADER
              <div className="lt-row lt-main">
                {/* Live / History Switcher */}
                <div className="segbar sm lt-mode" role="tablist">
                  <button
                    type="button"
                    className={mode === 'live' ? 'on' : ''}
                    onClick={() => {
                      setMode('live');
                      setFocusedReg(null);
                    }}
                  >
                    <span className="live-dot" />
                    Live
                  </button>
                  <button
                    type="button"
                    className={mode === 'history' ? 'on' : ''}
                  >
                    <span dangerouslySetInnerHTML={{ __html: ic('history', 'width="14" height="14"') }} />
                    History
                  </button>
                </div>

                {/* Tracking / Listing Switcher */}
                <div className="tabs sm vtog" role="tablist">
                  <button
                    type="button"
                    className={view === 'map' ? 'on' : ''}
                    onClick={() => setView('map')}
                  >
                    Tracking
                  </button>
                  <button
                    type="button"
                    className={view === 'list' ? 'on' : ''}
                    onClick={() => setView('list')}
                  >
                    Listing
                  </button>
                </div>

                {/* Vehicle Picker Button */}
                <div className="vp" id="vp">
                  <button
                    ref={vpBtnRef}
                    id="vpbtn"
                    type="button"
                    className="vp-btn"
                    onClick={handleOpenPickerPop}
                  >
                    <span dangerouslySetInnerHTML={{ __html: ic('truck') }} />
                    <span>{pickerBtnLabel()}</span>
                    {selectedVehicleRegs.length > 0 && (
                      <span className="n">({selectedVehicleRegs.length})</span>
                    )}
                    <span dangerouslySetInnerHTML={{ __html: ic('chev') }} />
                  </button>
                </div>

                {/* Period Selector */}
                <div className="segbar sm" id="hper">
                  {[
                    ['24h', 'Last 24h'],
                    ['today', 'Today'],
                    ['yesterday', 'Yesterday'],
                    ['7d', '7 days'],
                    ['14d', '14 days'],
                    ['30d', '30 days'],
                    ['custom', 'Custom'],
                  ].map(([perKey, perLabel]) => (
                    <button
                      key={perKey}
                      type="button"
                      className={historyPeriod === perKey ? 'on' : ''}
                      onClick={() => setHistoryPeriod(perKey)}
                    >
                      {perLabel}
                    </button>
                  ))}
                </div>

                {/* Custom Period Input */}
                {historyPeriod === 'custom' && (
                  <div className="d-flex align-items-center gap-1" id="hcus">
                    <input
                      type="date"
                      className="input sm"
                      value={customRange.from ? customRange.from.slice(0, 10) : ''}
                      onChange={(e) =>
                        setCustomRange({ ...customRange, from: e.target.value })
                      }
                    />
                    <span className="text-muted">–</span>
                    <input
                      type="date"
                      className="input sm"
                      value={customRange.to ? customRange.to.slice(0, 10) : ''}
                      onChange={(e) =>
                        setCustomRange({ ...customRange, to: e.target.value })
                      }
                    />
                  </div>
                )}

                {/* History Filter Popover Trigger */}
                <button
                  ref={hFilterBtnRef}
                  id="hftog"
                  type="button"
                  className={`btn sm lt-ftog ${activeFiltersCount > 0 ? 'has' : ''}`}
                  onClick={handleOpenFilterPop}
                >
                  <span dangerouslySetInnerHTML={{ __html: ic('filter', 'width="14" height="14"') }} />
                  <span className="tl">Filters</span>
                  {activeFiltersCount > 0 && <span className="fbadge">{activeFiltersCount}</span>}
                </button>

                {/* Actions / Export */}
                <div className="lt-actions" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExportAnchorEl(e.currentTarget);
                      setExportMenuOpen((prev) => !prev);
                    }}
                    title="Export options (PDF / CSV)"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: ic('report') }} />
                    <span className="tl">Export</span>
                  </button>

                  {exportMenuOpen && (
                    <div
                      className="export-dropdown-menu"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '6px',
                        background: '#ffffff',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '220px',
                        padding: '6px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleExportTopPdf}
                        disabled={exportingTopPdf}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1E293B',
                          background: 'none',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: exportingTopPdf ? 'wait' : 'pointer',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F5F9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <span dangerouslySetInnerHTML={{ __html: ic('report', 'width="14" height="14" stroke="#DC2626"') }} />
                        {exportingTopPdf
                          ? 'Generating PDF...'
                          : focusedReg
                          ? `PDF: ${focusedReg}`
                          : `PDF: All Vehicles (${historyScopeVehicles.length})`}
                      </button>

                      <div style={{ height: '1px', background: '#E2E8F0', margin: '4px 0' }} />

                      <button
                        type="button"
                        onClick={() => {
                          handleExportCSV();
                          setExportMenuOpen(false);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1E293B',
                          background: 'none',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F5F9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <span dangerouslySetInnerHTML={{ __html: ic('list', 'width="14" height="14" stroke="#16A34A"') }} />
                        Excel / CSV Dataset
                      </button>
                    </div>
                  )}
                </div>

                {/* Status Guide */}
                <span className="tb-help">
                  <button
                    type="button"
                    className="info-tip"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHelpModal(true);
                    }}
                    title="Status guide"
                  >
                    i
                  </button>
                </span>
              </div>
            )}

            {/* Active Filters Row */}
            {activeFiltersCount > 0 && (
              <div className="lt-row lt-fchips">
                {Object.entries(filters).map(([k, val]) => {
                  if (!val) return null;
                  return (
                    <span key={k} className="chip removable fchip">
                      {k}: {val}
                      <button
                        type="button"
                        className="chip-close"
                        onClick={() => setFilters({ ...filters, [k]: '' })}
                      >
                        ✕
                      </button>
                    </span>
                  );
                })}
                <button
                  type="button"
                  className="link fclear"
                  onClick={() =>
                    setFilters({
                      cat: '',
                      type: '',
                      cust: '',
                      drv: '',
                      prov: '',
                      trip: '',
                      tx: '',
                    })
                  }
                >
                  Clear all
                </button>
                <span className="muted fcount">
                  Showing{' '}
                  {mode === 'live'
                    ? filteredVehicles.length
                    : historyScopeVehicles.length}{' '}
                  of {vehicles.length}
                </span>
              </div>
            )}
          </div>

          {/* KPI Summary Strip */}
          <VehicleTrackingSummaryStrip
            mode={mode}
            vehicles={mode === 'live' ? filteredVehicles : vehicles}
            period={historyPeriod}
            customRange={customRange}
            fleetHistoryData={fleetHistoryData}
            activeFilterKey={activeKpiFilter}
            onFilterClick={(key) =>
              setActiveKpiFilter(activeKpiFilter === key ? '' : key)
            }
          />

          {/* Body Section */}
          <div className="lt-body" id="ltbody">
            {mode === 'live' ? (
              // ==========================================
              // LIVE MODE
              // ==========================================
              view === 'list' ? (
                // Live Listing View
                <VehicleTrackingList
                  vehicles={filteredVehicles}
                  onShowOnMap={(v) => {
                    setSelectedReg(v.reg);
                    setExpandedReg(v.reg);
                    setView('map');
                  }}
                  onOpenHistory={(reg) => {
                    setMode('history');
                    setView('map');
                    handleFocusHistoryVehicle(reg);
                  }}
                />
              ) : (
                // Live Tracking View (Left Panel + Map)
                <div className="live">
                  <div className="live-panel">
                    <div className="lp-list" id="lp-list" onScroll={handleLiveListScroll}>
                      {loadingVehicles && vehicles.length === 0 && (
                        <div style={{ padding: '24px 16px', textAlign: 'center', color: '#6366f1', fontSize: '12px' }}>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Loading live vehicles...
                        </div>
                      )}
                      {!loadingVehicles && filteredVehicles.length === 0 && (
                        <div style={{ padding: '32px 16px', textAlign: 'center', color: '#7A7C96', fontSize: '12px' }}>
                          <div style={{ fontWeight: 600, color: '#323338', marginBottom: '6px' }}>No vehicles found</div>
                          <div style={{ fontSize: '11px', marginBottom: '12px' }}>
                            {activeKpiFilter ? `Active filter: ${activeKpiFilter}` : 'Try adjusting your filters.'}
                          </div>
                          {(statusFilter !== 'all' || activeKpiFilter || Object.values(filters).some(Boolean)) && (
                            <button
                              type="button"
                              className="btn secondary sm"
                              onClick={() => {
                                setStatusFilter('all');
                                setActiveKpiFilter('');
                                setFilters({ cat: '', type: '', cust: '', drv: '', prov: '', trip: '', tx: '' });
                              }}
                            >
                              Reset filters
                            </button>
                          )}
                        </div>
                      )}
                      {filteredVehicles.slice(0, visibleLiveCount).map((v) => {
                        const i = info(v);
                        const isOpen = expandedReg === v.reg;
                        const km = keyMetric(v, i);
                        const tripMeta = typeof v.trip === 'string'
                          ? TRIP_META[v.trip]
                          : v.trip
                          ? {
                              id: v.trip.trip_id || `TRIP-${v.trip.id}`,
                              origin: v.trip.start_trip?.location || 'Origin',
                              dest: v.trip.end_trip?.location || 'Destination',
                            }
                          : null;
                        const tripSt = tripStatusOf(v);

                        const T_list = todaySplitOf(v, i);
                        const avgSpeedList = T_list.drive > 0
                          ? Math.round((v.km || 0) / (T_list.drive / 60))
                          : 0;

                        return (
                          <div
                            key={v.reg}
                            className={`lp-row ${isOpen ? 'open' : ''}`}
                            id={`row-${v.reg}`}
                          >
                            <div
                              className={`lp-item ${selectedReg === v.reg ? 'sel' : ''}`}
                              onClick={() => handleRowClickLive(v)}
                            >
                              <span className="mid">
                                <span className="l1">
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: typeIconSVG(v.type, i.display),
                                    }}
                                  />
                                  {v.reg}
                                </span>
                                <span className="l2 drv-line">
                                  <span
                                    className="drv-av"
                                    style={{
                                      width: '18px',
                                      height: '18px',
                                      background: '#E4DAFC',
                                      fontSize: '8px',
                                      display: 'inline-grid',
                                      placeItems: 'center',
                                      borderRadius: '50%',
                                    }}
                                  >
                                    {v.drv.slice(0, 2).toUpperCase()}
                                  </span>
                                  <span>{v.drv}</span>
                                </span>

                                {v.phone && (
                                  <a
                                    className="drv-tel"
                                    href={`tel:${v.phone.replace(/\s/g, '')}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: ic('phone', 'width="10" height="10"'),
                                      }}
                                    />
                                    <span>{v.phone}</span>
                                  </a>
                                )}

                                {v.trip ? (
                                  <span
                                    className={`trip-tag ${
                                      tripSt === 'In-Transit'
                                        ? 't-it'
                                        : tripSt === 'Completed'
                                        ? 't-cp'
                                        : 't-aw'
                                    }`}
                                  >
                                    <i />
                                    {tripSt}
                                    {tripMeta?.origin && tripMeta?.dest
                                      ? ` · ${tripMeta.origin.split(',')[0]} → ${
                                          tripMeta.dest.split(',')[0]
                                        }`
                                      : ''}
                                  </span>
                                ) : (
                                  <span className="trip-tag t-aw">
                                    <i />Awaiting for Trip
                                  </span>
                                )}
                              </span>

                              <span className="spd">
                                <span className={`spill sp-${i.display}`}>
                                  {STATUS_LABEL[i.display]}
                                </span>
                                <br />
                                <span
                                  className="num km"
                                  title={km.label}
                                  dangerouslySetInnerHTML={{ __html: km.html }}
                                />
                                {avgSpeedList > 0 && (
                                  <>
                                    <br />
                                    <span className="num km" title="Avg speed today" style={{ fontSize: '11px', color: '#6B7280' }}>
                                      ⌀ {avgSpeedList} km/h
                                    </span>
                                  </>
                                )}
                                {etaLine(v, i) && (
                                  <>
                                    <br />
                                    <span
                                      dangerouslySetInnerHTML={{ __html: etaLine(v, i) }}
                                    />
                                  </>
                                )}
                              </span>

                              <span
                                dangerouslySetInnerHTML={{ __html: ignChip(v, i) }}
                              />

                              {v.vio && v.vio.length > 0 && (
                                <span className="vio-b">
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: ic('warn', 'width="10" height="10"'),
                                    }}
                                  />
                                  <b>{v.vio.length}</b>
                                </span>
                              )}

                              <span
                                className="chev"
                                dangerouslySetInnerHTML={{
                                  __html: ic(
                                    'chev',
                                    `style="transform: rotate(${isOpen ? 180 : 0}deg)"`
                                  ),
                                }}
                              />
                            </div>

                            {/* Inline Expanded Card */}
                            {isOpen && (
                              <VehicleTrackingCard
                                vehicle={v}
                                initialTelemetry={telemetryCache[v.id]?.tele}
                                initialDetail={telemetryCache[v.id]?.det}
                                followVehicle={followVehicle}
                                onToggleFollow={setFollowVehicle}
                                onTelemetryLoaded={(tele, det) => {
                                  setTelemetryCache((prev) => ({
                                    ...prev,
                                    [v.id]: { tele, det },
                                  }));
                                  if (tele?.current_status) {
                                    setVehicles((prev) =>
                                      prev.map((veh) =>
                                        veh.id === v.id
                                          ? mergeVehicleWithViewData(veh, {
                                              ...veh.raw,
                                              vehicle_current_detail: tele.current_status,
                                              last_log: tele.current_status,
                                              polyline: tele.polyline,
                                            })
                                          : veh
                                      )
                                    );
                                  }
                                }}
                                onShowTripOnMap={(reg) => handleShowTripOnMap(reg || v.reg)}
                                onViewHistory={() => {
                                  setMode('history');
                                  setView('map');
                                  handleFocusHistoryVehicle(v.reg);
                                }}
                                onClose={() => handleRowClickLive(v)}
                              />
                            )}
                          </div>
                        );
                      })}

                      {visibleLiveCount < filteredVehicles.length && (
                        <div
                          className="lp-load-more"
                          onClick={() => setVisibleLiveCount((prev) => Math.min(prev + 20, filteredVehicles.length))}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontSize: '11.5px',
                            color: '#4014B8',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: '#F8F6FF',
                            borderTop: '1px solid #ECE7FC',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                          }}
                        >
                          <span>Showing {visibleLiveCount} of {filteredVehicles.length} vehicles · Scroll for more</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <VehicleTrackingMap
                    mode="live"
                    vehicles={filteredVehicles}
                    selectedVehicle={selectedVehicle}
                    onSelectVehicle={handleSelectLiveVehicle}
                    followVehicle={followVehicle}
                    onToggleFollow={setFollowVehicle}
                    flyTrigger={flyTrigger}
                    triggerFitTrip={triggerFitTrip}
                    mapOnly={mapOnly}
                    onToggleMapOnly={() => setMapOnly(!mapOnly)}
                  />
                </div>
              )
            ) : (
              // ==========================================
              // HISTORY MODE
              // ==========================================
              view === 'list' ? (
                // History Listing View (Fleet Master Table or Single Vehicle Segments)
                <VehicleTrackingHistory
                  vehicles={historyScopeVehicles}
                  focusedVehicle={focusedVehicleObj}
                  historyData={focusedHistoryData}
                  fleetDataList={fleetHistoryData}
                  loading={focusedVehicleObj ? loadingHistory : loadingHistorySummary}
                  error={focusedVehicleObj ? historyError : historySummaryError}
                  onRetry={() => setHistoryReloadTrigger((prev) => prev + 1)}
                  period={historyPeriod}
                  customRange={customRange}
                  hview="list"
                  onSelectVehicle={handleFocusHistoryVehicle}
                  onShowAllVehicles={handleBackToFleetHistory}
                  telemetryData={focusedVehicleObj?.id ? telemetryCache[focusedVehicleObj.id]?.tele : null}
                  detailData={focusedVehicleObj?.id ? telemetryCache[focusedVehicleObj.id]?.det : null}
                  onRefreshTelemetry={(v) => fetchTelemetryAndDetail(v?.id || focusedVehicleObj?.id)}
                  onShowTripOnMap={handleShowTripOnMap}
                />
              ) : (
                // History Tracking View (Fleet or Focused Vehicle with Playback)
                <div className="live">
                  <div className="live-panel">
                    <VehicleTrackingHistory
                      vehicles={historyScopeVehicles}
                      focusedVehicle={focusedVehicleObj}
                      historyData={focusedHistoryData}
                      fleetDataList={fleetHistoryData}
                      loading={focusedVehicleObj ? loadingHistory : loadingHistorySummary}
                      error={focusedVehicleObj ? historyError : historySummaryError}
                      onRetry={() => setHistoryReloadTrigger((prev) => prev + 1)}
                      period={historyPeriod}
                      customRange={customRange}
                      hview="map"
                      onSelectVehicle={handleFocusHistoryVehicle}
                      onShowAllVehicles={handleBackToFleetHistory}
                      playbackStep={playbackStep}
                      onSeekPlayback={setPlaybackStep}
                      hoveredReg={historyHoveredReg}
                      onHoverReg={setHistoryHoveredReg}
                      telemetryData={focusedVehicleObj?.id ? telemetryCache[focusedVehicleObj.id]?.tele : null}
                      detailData={focusedVehicleObj?.id ? telemetryCache[focusedVehicleObj.id]?.det : null}
                      onRefreshTelemetry={(v) => fetchTelemetryAndDetail(v?.id || focusedVehicleObj?.id)}
                      onShowTripOnMap={handleShowTripOnMap}
                    />
                  </div>

                  <VehicleTrackingMap
                    mode="history"
                    vehicles={historyScopeVehicles}
                    focusedVehicle={focusedVehicleObj}
                    historyData={focusedHistoryData}
                    fleetHistoryData={fleetHistoryData}
                    hoveredReg={historyHoveredReg}
                    onHoverReg={setHistoryHoveredReg}
                    onSelectVehicle={handleFocusHistoryVehicle}
                    playbackStep={playbackStep}
                    onSeekPlayback={setPlaybackStep}
                    isPlaying={isPlaying}
                    onTogglePlay={() => setIsPlaying(!isPlaying)}
                    playbackSpeed={playbackSpeed}
                    onChangePlaybackSpeed={setPlaybackSpeed}
                    mapOnly={mapOnly}
                    onToggleMapOnly={() => setMapOnly(!mapOnly)}
                  />
                </div>
              )
            )}
          </div>
        </div>

      {/* Filter Popover Modal */}
      {filterPopOpen && (
        <VehicleTrackingFilterPop
          anchorEl={filterAnchorEl}
          isOpen={filterPopOpen}
          onClose={() => setFilterPopOpen(false)}
          filters={filters}
          onApplyFilters={setFilters}
          vehicles={vehicles}
        />
      )}

      {/* Vehicle Picker Modal (History) */}
      {pickerPopOpen && (
        <VehicleTrackingVehiclePicker
          anchorEl={pickerAnchorEl}
          isOpen={pickerPopOpen}
          onClose={() => setPickerPopOpen(false)}
          vehicles={historyPickerVehicles}
          selectedRegs={selectedVehicleRegs}
          onApply={(regs) => {
            setSelectedVehicleRegs(regs);
            if (regs && regs.length === 1) {
              handleFocusHistoryVehicle(regs[0]);
            } else if (!regs || regs.length === 0) {
              setFocusedReg(null);
            }
          }}
        />
      )}

      {/* Status Guide Modal */}
      <CustomModal
        show={showHelpModal}
        handleClose={() => setShowHelpModal(false)}
        heading="Vehicle Tracking Status & Rules Guide"
        size="lg"
      >
        <div style={{ padding: '4px 8px', fontSize: '13px', lineHeight: '1.6' }}>
          <h4 style={{ fontWeight: '700', marginBottom: '8px', color: '#10052E' }}>
            Movement Statuses
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            <div style={{ border: '1px solid #E2E1EC', borderRadius: '8px', padding: '10px' }}>
              <span className="spill sp-moving" style={{ marginBottom: '6px' }}>Moving</span>
              <p style={{ margin: 0, fontSize: '12px', color: '#5C5F80' }}>
                Vehicle is in motion (GPS speed &gt; 0 km/h) and reporting telemetry.
              </p>
            </div>
            <div style={{ border: '1px solid #E2E1EC', borderRadius: '8px', padding: '10px' }}>
              <span className="spill sp-idle" style={{ marginBottom: '6px' }}>Idle</span>
              <p style={{ margin: 0, fontSize: '12px', color: '#5C5F80' }}>
                Ignition is ON, but vehicle is stationary (0 km/h).
              </p>
            </div>
            <div style={{ border: '1px solid #E2E1EC', borderRadius: '8px', padding: '10px' }}>
              <span className="spill sp-stopped" style={{ marginBottom: '6px' }}>Stopped</span>
              <p style={{ margin: 0, fontSize: '12px', color: '#5C5F80' }}>
                Ignition is OFF and vehicle is parked.
              </p>
            </div>
            <div style={{ border: '1px solid #E2E1EC', borderRadius: '8px', padding: '10px' }}>
              <span className="spill sp-inactive" style={{ marginBottom: '6px' }}>Inactive</span>
              <p style={{ margin: 0, fontSize: '12px', color: '#5C5F80' }}>
                GPS device has not transmitted an update recently or vehicle is out of service.
              </p>
            </div>
          </div>

          <h4 style={{ fontWeight: '700', marginBottom: '8px', color: '#10052E' }}>
            Violation Types
          </h4>
          <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '12.5px', color: '#5C5F80' }}>
            <li><b>Overspeeding:</b> Exceeding speed limits (HTV &gt; 65 km/h, LTV &gt; 90 km/h).</li>
            <li><b>Harsh Braking:</b> Sudden deceleration greater than 0.35 g.</li>
            <li><b>Harsh Acceleration:</b> Rapid acceleration greater than 0.30 g.</li>
            <li><b>Sharp Cornering:</b> Lateral acceleration greater than 0.30 g.</li>
            <li><b>Long Idling:</b> Continuous idling exceeding 30 minutes.</li>
          </ul>
        </div>
      </CustomModal>
    </div>
  );
};

export default VehicleTracking;
